import React, { useState, useEffect } from 'react';
import { submitChildEducationForm } from '../services/api';
import '../css/ChildEducationForm.css';
import ChildEducationFormTranslations from '../components/ChildEducationFormTranslations';

const ChildEducation = ({ onGoalComplete, onBackToGoals }) => {
  const currentLanguage = 'en';
  const translations = ChildEducationFormTranslations[currentLanguage];

  // Form states
  const [numChildren, setNumChildren] = useState('');
  const [children, setChildren] = useState([]);
  const [usdToInr, setUsdToInr] = useState(80);
  const [totalCost, setTotalCost] = useState(0);
  const [exchangeRateMessage, setExchangeRateMessage] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [childrenErrors, setChildrenErrors] = useState([]);
  const [expectedReturn, setExpectedReturn] = useState(12); // Default expected return
  const [totalSIP, setTotalSIP] = useState(0);

  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        const rate = data.rates.INR;
        setUsdToInr(rate);
        setExchangeRateMessage(
          `${translations.labels.usd_to_inr_rate_is} ${rate.toFixed(2)} ${translations.labels.fetched_live}.`
        );
      } catch (error) {
        console.error(translations.errors.failedToFetchExchangeRate || 'Unable to fetch live USD to INR rate. Using default rate: 80.');
        setExchangeRateMessage(
          `${translations.labels.usd_to_inr_rate_is} 80.00 (${translations.labels.default_value}).`
        );
      }
    };

    fetchExchangeRate();
  }, []);

  useEffect(() => {
    if (numChildren) {
      const num = parseInt(numChildren);
      if (!isNaN(num) && num >= 0 && num <= 5) {
        setChildren(Array(num).fill().map(() => ({
          name: '',
          gender: translations.data.genderOptions[0]?.value || 'Male',
          age: '',
          ugCostAbroadUsd: '',
          ugCostIndiaInr: '',
          mastersCostAbroadUsd: '',
          mastersCostIndiaInr: '',
        })));
        setChildrenErrors(Array(num).fill({}));
      }
    }
  }, [numChildren]);

  const handleChildChange = (index, field, value) => {
    const updatedChildren = [...children];
    updatedChildren[index] = {
      ...updatedChildren[index],
      [field]: value
    };

    // Clear related fields when one is set
    if (field === 'ugCostAbroadUsd' && value) {
      updatedChildren[index]['ugCostIndiaInr'] = '';
    } else if (field === 'ugCostIndiaInr' && value) {
      updatedChildren[index]['ugCostAbroadUsd'] = '';
    } else if (field === 'mastersCostAbroadUsd' && value) {
      updatedChildren[index]['mastersCostIndiaInr'] = '';
    } else if (field === 'mastersCostIndiaInr' && value) {
      updatedChildren[index]['mastersCostAbroadUsd'] = '';
    }

    setChildren(updatedChildren);

    // Clear error for this field
    if (childrenErrors[index]?.[field]) {
      const updatedChildErrors = [...childrenErrors];
      updatedChildErrors[index] = { ...updatedChildErrors[index], [field]: undefined };
      setChildrenErrors(updatedChildErrors);
    }
  };

  const validateChildrenDetails = () => {
    let valid = true;
    const newChildrenErrors = children.map(() => ({}));

    children.forEach((child, index) => {
      const childErrors = {};

      // Validate name
      if (!child.name.trim()) {
        childErrors.name = translations.errors.childNameRequired || 'Child name is required';
        valid = false;
      }

      // Validate age
      const age = parseInt(child.age);
      if (isNaN(age)) {
        childErrors.age = translations.errors.invalidChildAge || 'Age is required';
        valid = false;
      } else if (age < 1 || age > 15) {
        childErrors.age = translations.errors.invalidChildAge || 'Age must be between 1 and 15';
        valid = false;
      }

      // Validate UG cost
      if (!child.ugCostAbroadUsd && !child.ugCostIndiaInr) {
        childErrors.ugCost = translations.errors.ugCostRequired || 'Please provide UG cost';
        valid = false;
      } else {
        if (child.ugCostAbroadUsd && isNaN(parseFloat(child.ugCostAbroadUsd))) {
          childErrors.ugCostAbroadUsd = translations.errors.invalidUGCostAbroad || 'Invalid UG cost abroad';
          valid = false;
        }
        if (child.ugCostIndiaInr && isNaN(parseFloat(child.ugCostIndiaInr))) {
          childErrors.ugCostIndiaInr = translations.errors.invalidUGCostIndia || 'Invalid UG cost in India';
          valid = false;
        }
      }

      // Validate Masters cost
      if (!child.mastersCostAbroadUsd && !child.mastersCostIndiaInr) {
        childErrors.mastersCost = translations.errors.mastersCostRequired || 'Please provide Masters cost';
        valid = false;
      } else {
        if (child.mastersCostAbroadUsd && isNaN(parseFloat(child.mastersCostAbroadUsd))) {
          childErrors.mastersCostAbroadUsd = translations.errors.invalidMastersCostAbroad || 'Invalid Masters cost abroad';
          valid = false;
        }
        if (child.mastersCostIndiaInr && isNaN(parseFloat(child.mastersCostIndiaInr))) {
          childErrors.mastersCostIndiaInr = translations.errors.invalidMastersCostIndia || 'Invalid Masters cost in India';
          valid = false;
        }
      }

      newChildrenErrors[index] = childErrors;
    });

    setChildrenErrors(newChildrenErrors);
    return valid;
  };

  // Calculate SIP required for a future amount
  const calculateSIP = (futureValue, years, annualReturn) => {
    if (years <= 0 || annualReturn <= 0) return 0;
    
    const monthlyRate = annualReturn / 100 / 12;
    const months = years * 12;
    
    // SIP = FV * r / [(1+r)^n - 1]
    const sip = futureValue * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1);
    return sip;
  };

  const calculateEducationCosts = () => {
    if (!validateChildrenDetails()) {
      alert(translations.errors.validationFailed || "Please correct form errors");
      return;
    }

    let total = 0;
    let totalSIPValue = 0;
    const calculatedResults = [];

    children.forEach((child) => {
      const age = parseInt(child.age);
      const yearsToUG = Math.max(0, 18 - age);
      const yearsToMasters = Math.max(0, 22 - age);

      // Determine inflation rates based on education location
      const ugInflation = child.ugCostAbroadUsd ? 0.075 : 0.12;
      const mastersInflation = child.mastersCostAbroadUsd ? 0.075 : 0.12;

      // Calculate UG cost
      let ugCost = 0;
      if (child.ugCostAbroadUsd) {
        ugCost = parseFloat(child.ugCostAbroadUsd) * 
                 Math.pow(1 + ugInflation, yearsToUG) * 
                 usdToInr;
      } else if (child.ugCostIndiaInr) {
        ugCost = parseFloat(child.ugCostIndiaInr) * 
                 Math.pow(1 + ugInflation, yearsToUG);
      }

      // Calculate Masters cost
      let mastersCost = 0;
      if (child.mastersCostAbroadUsd) {
        mastersCost = parseFloat(child.mastersCostAbroadUsd) * 
                      Math.pow(1 + mastersInflation, yearsToMasters) * 
                      usdToInr;
      } else if (child.mastersCostIndiaInr) {
        mastersCost = parseFloat(child.mastersCostIndiaInr) * 
                      Math.pow(1 + mastersInflation, yearsToMasters);
      }

      const childTotal = ugCost + mastersCost;
      total += childTotal;

      // Calculate SIPs
      const ugSIP = calculateSIP(ugCost, yearsToUG, expectedReturn);
      const mastersSIP = calculateSIP(mastersCost, yearsToMasters, expectedReturn);
      const childTotalSIP = ugSIP + mastersSIP;
      totalSIPValue += childTotalSIP;

      calculatedResults.push({
        name: child.name,
        age: child.age,
        yearsToUG,
        yearsToMasters,
        ugCost,
        mastersCost,
        childTotal,
        ugSIP,
        mastersSIP,
        childTotalSIP
      });
    });

    setTotalCost(total);
    setTotalSIP(totalSIPValue);
    setResults(calculatedResults);
    setShowResults(true);
  };

  const handleSubmitForm = async () => {
    if (!showResults) {
      alert("Please calculate costs before submitting");
      return;
    }

    const agentId = localStorage.getItem("agentId");
    const formData = {
      exchangeRate: usdToInr,
      totalEducationExpenses: totalCost,
      children: children.map(child => ({
        name: child.name,
        gender: child.gender,
        age: parseInt(child.age),
        ugCostAbroadUsd: child.ugCostAbroadUsd ? parseFloat(child.ugCostAbroadUsd) : 0,
        ugCostIndiaInr: child.ugCostIndiaInr ? parseFloat(child.ugCostIndiaInr) : 0,
        mastersCostAbroadUsd: child.mastersCostAbroadUsd ? parseFloat(child.mastersCostAbroadUsd) : 0,
        mastersCostIndiaInr: child.mastersCostIndiaInr ? parseFloat(child.mastersCostIndiaInr) : 0,
      })),
    };

    try {
      console.log("Child education form data:", formData);
      onGoalComplete(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const formatCurrency = (value) => {
    if (!value || value <= 0) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="goal-planning-section">
      <div className="child-education-form">
        <div className="form-section">
          <h2 style={{ color: 'rgb(112, 16, 99)' }}>{translations.labels.title || "Child Education Planning"}</h2>
          <div className="form-row">
            <div className="form-group">
              <label>{translations.labels.number_of_children}:</label>
              <select
                value={numChildren}
                onChange={(e) => setNumChildren(e.target.value)}
                className="form-select"
              >
                <option value="">Select number</option>
                {[0, 1, 2, 3, 4, 5].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>{translations.labels.expected_return}: (%):</label>
              <input
                type="number"
                value={expectedReturn}
                onChange={(e) => setExpectedReturn(e.target.value)}
                min="0"
                max="30"
                step="0.1"
                className="form-input"
              />
            </div>
          </div>

          {children.length > 0 && (
            <div className="children-details">
            <h3>{translations.labels.child_detail || "Children's Details"}</h3>
              {children.map((child, index) => (
                <div key={index} className="child-form-section">
                  <h3>{translations.labels.child_number} {index + 1}</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{translations.labels.child_name}:</label>
                      <input
                        type="text"
                        value={child.name}
                        onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                        placeholder={translations.placeholders.child_name}
                        className="form-input"
                      />
                      {childrenErrors[index]?.name && 
                        <p className="error">{childrenErrors[index].name}</p>}
                    </div>

                    <div className="form-group">
                      <label>{translations.labels.gender}:</label>
                      <select
                        value={child.gender}
                        onChange={(e) => handleChildChange(index, 'gender', e.target.value)}
                        className="form-select"
                      >
                        {translations.data.genderOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{translations.labels.age} (1-15):</label>
                      <input
                        type="number"
                        min="1"
                        max="15"
                        value={child.age}
                        onChange={(e) => handleChildChange(index, 'age', e.target.value)}
                        className="form-input"
                      />
                      {childrenErrors[index]?.age && 
                        <p className="error">{childrenErrors[index].age}</p>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{translations.labels.ugCostAbroadUsd}:</label>
                      <input
                        type="number"
                        min="0"
                        value={child.ugCostAbroadUsd}
                        onChange={(e) => handleChildChange(index, 'ugCostAbroadUsd', e.target.value)}
                        disabled={!!child.ugCostIndiaInr}
                        className="form-input"
                        placeholder="UG abroad (USD)"
                      />
                      {childrenErrors[index]?.ugCostAbroadUsd && 
                        <p className="error">{childrenErrors[index].ugCostAbroadUsd}</p>}
                    </div>

                    <div className="form-group">
                      <label>{translations.labels.ugCostIndiaInr}:</label>
                      <input
                        type="number"
                        min="0"
                        value={child.ugCostIndiaInr}
                        onChange={(e) => handleChildChange(index, 'ugCostIndiaInr', e.target.value)}
                        disabled={!!child.ugCostAbroadUsd}
                        className="form-input"
                        placeholder="UG India (INR)"
                      />
                      {childrenErrors[index]?.ugCostIndiaInr && 
                        <p className="error">{childrenErrors[index].ugCostIndiaInr}</p>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{translations.labels.mastersCostAbroadUsd}:</label>
                      <input
                        type="number"
                        min="0"
                        value={child.mastersCostAbroadUsd}
                        onChange={(e) => handleChildChange(index, 'mastersCostAbroadUsd', e.target.value)}
                        disabled={!!child.mastersCostIndiaInr}
                        className="form-input"
                        placeholder="Masters abroad (USD)"
                      />
                      {childrenErrors[index]?.mastersCostAbroadUsd && 
                        <p className="error">{childrenErrors[index].mastersCostAbroadUsd}</p>}
                    </div>

                    <div className="form-group">
                      <label>{translations.labels.mastersCostIndiaInr}:</label>
                      <input
                        type="number"
                        min="0"
                        value={child.mastersCostIndiaInr}
                        onChange={(e) => handleChildChange(index, 'mastersCostIndiaInr', e.target.value)}
                        disabled={!!child.mastersCostAbroadUsd}
                        className="form-input"
                        placeholder="Masters India (INR)"
                      />
                      {childrenErrors[index]?.mastersCostIndiaInr && 
                        <p className="error">{childrenErrors[index].mastersCostIndiaInr}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showResults && results.length > 0 && (
            <div className="results-section">
              <h3>{translations.labels.results}</h3>
              {results.map((result, index) => (
                <div key={index} className="child-result">
                  <h4>{result.name} (Age: {result.age})</h4>
                  <div className="result-grid">
                    <div>
                      <p>Years to UG: {result.yearsToUG}</p>
                      <p>UG Cost: {formatCurrency(result.ugCost)}</p>
                      <p>UG SIP: {formatCurrency(result.ugSIP)}/month</p>
                    </div>
                    <div>
                      <p>Years to Masters: {result.yearsToMasters}</p>
                      <p>Masters Cost: {formatCurrency(result.mastersCost)}</p>
                      <p>Masters SIP: {formatCurrency(result.mastersSIP)}/month</p>
                    </div>
                  </div>
                  <p className="total-sip">Total SIP for {result.name}: {formatCurrency(result.childTotalSIP)}/month</p>
                </div>
              ))}
              <div className="total-result">
                <h3>Total Education Expenses: {formatCurrency(totalCost)}</h3>
                <h3>Total SIP Required: {formatCurrency(totalSIP)}/month</h3>
              </div>
            </div>
          )}

          <div className="action-buttons">
            <button 
              className="submit-button"
              onClick={calculateEducationCosts}
              disabled={children.length === 0}
            >
              Calculate
            </button>
            
            <button 
              className="submit-button"
              onClick={onBackToGoals}
            >
              ← Back to Goals
            </button>
            
            <button 
              className="submit-button"
              onClick={handleSubmitForm}
              disabled={!showResults}
            >
              Next
            </button>
          </div>
        </div>

        <div className="assumptions">
          <strong>Assumptions:</strong>
          <ul>
            <li>Education Inflation in India: 12%</li>
            <li>Education Inflation Abroad: 7.5%</li>
            <li>Age for UG: 18 years</li>
            <li>Age for Masters: 22 years</li>
            <li>{exchangeRateMessage}</li>
            <li>Expected Annual Return: {expectedReturn}%</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChildEducation;